import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import block from 'bem-cn';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';

import SVG from 'components/SVG';
import InputSearch from 'components/InputSearch/InputSearch';
import Spinner from 'components/Spinner/desktop';

import { actions } from '../../redux';
import TreeRow from './TreeRow/TreeRow';

import arrowSvg from '../img/arrow.svg';
import userSvg from '../img/user.svg';
import backSvg from '../img/back.svg';

import './Tree.scss';

const b = block('tree-desktop');

const Tree = ({ callback }) => {
  const dispatch = useDispatch();
  const {
    users: usersDataList,
    selectedAgentId,
    actionProcessing,
    parentTree,
    searchTree,
  } = useSelector(state => state.tree);
  const isFirstParentOpened = !!usersDataList.length && !!usersDataList[0].isOpened;
  const [isOpened, setOpened] = useState(false);
  const locale = useSelector(state => state.locale.locale, shallowEqual);
  const auth = useSelector(state => state.auth);

  const [search, setSearch] = useState('');

  const searchRef = useRef(search);

  const handleSearchChange = value => {
    setSearch(value);
  };

  const selectAgent = useCallback(
    id => {
      dispatch(actions.selectAgentId(id));
    },
    [dispatch],
  );
  const rows = useMemo(() => {
    if (search) {
      return parentTree?.map((row, idx, arr) => (
        <TreeRow
          key={`${row.userId}-${idx}`}
          nesting={[row.userId]}
          row={row}
          isLast={arr.length - 1 === idx}
          idx={idx}
          level={1}
          userList={arr}
          search={search}
        />
      ));
    }
    return usersDataList.map((row, idx, arr) => (
      <TreeRow
        key={`${row.userId}-${idx}`}
        nesting={[row.userId]}
        row={row}
        isLast={arr.length - 1 === idx}
        idx={idx}
        level={1}
        userList={arr}
        search={search}
      />
    ));
  }, [parentTree, search, usersDataList]);

  useEffect(() => {
    isFirstParentOpened && setOpened(isFirstParentOpened);
  }, [isFirstParentOpened]);

  useEffect(() => {
    handleSearchChange(searchTree);
  }, [searchTree]);

  useEffect(() => {
    const mergeWithParentTree = Boolean(searchRef.current) && selectedAgentId !== auth.id;
    searchRef.current = search;

    dispatch(actions.getUsersByUserId({ search, mergeWithParentTree }));

    if (!mergeWithParentTree) selectAgent(auth.id);
  }, [search]);

  const onClick = useCallback(() => {
    selectAgent(auth.id);
    if (usersDataList.length !== 0) {
      setOpened(prevState => !prevState);
    } else {
      setOpened(false);
    }
  }, [usersDataList.length, auth.id, selectAgent]);

  const active = selectedAgentId === auth.id;

  const CurrentAgent = (
    <div className={b('current-agent')} onClick={onClick}>
      <div className={b('current-agent', 'wrapper')}>
        <SVG svgProps={{ svg: userSvg }} className={b('current-agent', 'img-user', { opened: isOpened, active })} />
        <span className={b('current-agent', 'title', { opened: isOpened, active })}>{auth.username}</span>
      </div>
      {!!usersDataList.length && (
        <SVG svgProps={{ svg: arrowSvg }} className={b('current-agent', 'img-arrow', { opened: isOpened, active })} />
      )}
    </div>
  );

  return (
    <div className={b({ active })}>
      <Spinner isLoading={actionProcessing} />
      <div className={b('title')} onClick={callback ? callback : () => null}>
        {callback && <SVG svgProps={{ svg: backSvg }} className={b('back-arrow')} />}
        {locale.agents}
      </div>
      <div className={b('search-title')}>{locale.search}</div>
      <div className={b('search')}>
        <InputSearch value={searchTree} onChange={handleSearchChange} placeholder={locale.search} />
      </div>
      {usersDataList[0]?.userId !== auth.id && CurrentAgent}
      {(isOpened || search) && <div className={b('wrapper')}>{rows}</div>}
    </div>
  );
};

export default Tree;
